import { useCallback, useEffect, useState } from "react";
import { Workflow, Loader2, CheckCircle2, Clock, XCircle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";

function WfCard({ name, w }) {
  return (
    <div className="rounded-2xl border border-white/8 bg-[#121214] p-5" data-testid={`wf-card-${name.toLowerCase()}`}>
      <div className="flex items-center justify-between mb-4">
        <span className="text-white font-medium">{name}</span>
        <span className="text-[#D4AF37] font-heading text-lg">{w.completion_rate}%</span>
      </div>
      <div className="space-y-2 text-sm">
        <div className="flex items-center gap-2 text-emerald-400"><CheckCircle2 className="w-4 h-4" /><span className="text-[#A1A1AA]">Completed</span><span className="ml-auto text-white">{w.completed}</span></div>
        <div className="flex items-center gap-2 text-amber-400"><Clock className="w-4 h-4" /><span className="text-[#A1A1AA]">In progress</span><span className="ml-auto text-white">{w.in_progress}</span></div>
        <div className="flex items-center gap-2 text-red-400"><XCircle className="w-4 h-4" /><span className="text-[#A1A1AA]">Abandoned</span><span className="ml-auto text-white">{w.abandoned}</span></div>
      </div>
      <p className="text-[11px] text-[#52525B] mt-3">Avg completion: {w.avg_completion_days} days</p>
    </div>
  );
}

export default function AdminWorkflows() {
  const [d, setD] = useState(null);
  const [denied, setDenied] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/workflows").then((r) => setD(r.data)).catch((e) => { if (e.response?.status === 403) setDenied(true); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (module, enabled) => {
    try { await api.put("/admin/workflows/modules", { module, enabled }); toast.success(`${module} ${enabled ? "enabled" : "disabled"}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (denied) return <p className="text-sm text-[#71717A]" data-testid="admin-workflows-denied">You don't have the "workflow.view" permission.</p>;
  if (!d) return <div className="flex justify-center py-20 text-[#71717A]"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div data-testid="admin-workflows" className="space-y-8">
      <div>
        <div className="flex items-center gap-2 mb-4"><Workflow className="w-5 h-5 text-[#D4AF37]" /><h3 className="text-white font-medium">Workflow completion</h3></div>
        <div className="grid sm:grid-cols-3 gap-4">
          <WfCard name="Formation" w={d.workflows.formation} />
          <WfCard name="Credit" w={d.workflows.credit} />
          <WfCard name="Funding" w={d.workflows.funding} />
        </div>
      </div>
      <div>
        <h3 className="text-white font-medium mb-3">Module availability</h3>
        <div className="rounded-2xl border border-white/8 bg-[#121214] divide-y divide-white/5" data-testid="wf-modules">
          {d.modules.map((m) => (
            <div key={m.module} className="flex items-center justify-between px-5 py-3">
              <span className="text-[#D4D4D8] capitalize">{m.module}</span>
              <Switch checked={m.enabled} onCheckedChange={(v) => toggle(m.module, v)} data-testid={`wf-toggle-${m.module}`} />
            </div>
          ))}
        </div>
        <p className="text-[11px] text-[#52525B] mt-2">Disabling a module hides it from users' navigation.</p>
      </div>
    </div>
  );
}
